<template>
    <section :class="[{ 'has-scrollbar': hasScrollbar }, sourceClass]" ref="container">
        <form :action="action" method="POST" target="_parent" @submit.prevent="submit" v-resize="onResize" ref="form">
            <input type="hidden" name="Source" :value="source">
            <fieldset>
                <h1>Start de huisscan van <img src="logoHQG.svg" alt="HomeQgo" /></h1>
                <b-field grouped>
                    <b-field
                        label="Postcode"
                        :type="{
                            'is-danger': $v.postCode.$error,
                            'is-success': $v.postCode.$dirty && !$v.postCode.$error
                        }">
                        <b-input
                            v-model.lazy="postCode"
                            @blur="$v.postCode.$touch()"
                            aria-required="true"
                            placeholder="1234 AB"
                            name="PostCode"
                            data-cy="postCode" />
                    </b-field>
                    <b-field
                        label="Huisnummer"
                        :type="{
                            'is-danger': $v.houseNumber.$error,
                            'is-success': $v.houseNumber.$dirty && !$v.houseNumber.$error
                        }">
                        <b-input
                            v-model.lazy="houseNumber"
                            @blur="$v.houseNumber.$touch()"
                            aria-required="true"
                            name="HouseNumber"
                            data-cy="houseNumber" />
                    </b-field>
                </b-field>

                <b-field label="Toevoeging">
                    <b-input v-model="houseNumberAddition" name="HouseNumberExtension" />
                </b-field>

                <b-field
                    :type="{
                        'is-danger': $v.firstName.$error,
                        'is-success': $v.firstName.$dirty && !$v.firstName.$error
                    }"
                    label="Voornaam">
                    <b-input
                        v-model.lazy="firstName"
                        @blur="$v.firstName.$touch()"
                        aria-required="true"
                        name="FirstName"
                        data-cy="firstName" />
                </b-field>

                <b-field
                    :type="{
                        'is-danger': $v.emailAddress.$error,
                        'is-success': $v.emailAddress.$dirty && !$v.emailAddress.$error
                    }"
                    label="E-mailadres">
                    <b-input
                        v-model.lazy="emailAddress"
                        @blur="$v.emailAddress.$touch()"
                        aria-required="true"
                        name="Email"
                        data-cy="emailAddress" />
                </b-field>

                <b-field>
                    <b-checkbox
                        v-model.lazy="consent"
                        @blur="$v.consent.$touch()"
                        :class="{ 'is-danger': $v.consent.$error }"
                        role="checkbox"
                        aria-required="true"
                        aria-checked="false"
                        data-cy="consent">
                        Ik ga akkoord met de <a href="https://www.homeqgo.nl/voorwaarden" target="_blank">voorwaarden</a> en geef HomeQgo toestemming om mijn gegevens te gebruiken voor een persoonlijk aanbod en mij hierover per e-mail te informeren.
                    </b-checkbox>
                </b-field>

                <ul v-if="hasError" class="errors">
                    <li v-if="$v.postCode.$error">Wil je je postcode invullen?</li>
                    <li v-if="$v.houseNumber.$error">Wil je je huisnummer invullen?</li>
                    <li v-if="$v.firstName.$error">Wil je je voornaam invullen?</li>
                    <li v-if="$v.emailAddress.$error">Wil je je e-mailadres invullen?</li>
                    <li v-if="$v.consent.$error">Wil je akkoord geven op de algemene voorwaarden?</li>
                </ul>

                <b-button @click="submit" class="has-text-weight-bold">
                    Kijk direct wat jij kunt besparen
                </b-button>
            </fieldset>
        </form>
    </section>
</template>

<script>
import resize from 'vue-resize-directive';
import { required, email, helpers } from 'vuelidate/lib/validators';

const mustBeTrue = value => value === true;
const isPostCode = helpers.regex('postcode', /^[0-9]{4}\s?[a-z]{2}$/i);

export default {
    directives: {
        resize
    },
    data() {
        const defaultParams = {
            source: '',
            action: 'https://update.homeqgo.nl/refer'
        };

        const params = { ...defaultParams, ...Object.fromEntries(new URLSearchParams(window.location.search)) };
        return {
            postCode: '',
            houseNumber: '',
            houseNumberAddition: '',
            firstName: '',
            emailAddress: '',
            consent: false,
            source: params.source,
            action: params.action,
            hasScrollbar: false
        };
    },
    computed: {
        hasError() {
            return Object.keys(this.$v.$params).some(k => this.$v[k].$error);
        },
        sourceClass() {
            return this.source ? `is-source-${this.source}` : '';
        }
    },
    methods: {
        submit() {
            this.$v.$touch();
            if (!this.$v.$invalid) {
                this.$refs.form.submit();
            }
        },
        onResize() {
            const { container, form } = this.$refs;
            this.hasScrollbar = form.clientHeight > container.clientHeight;
        }
    },
    validations: {
        postCode: {
            required,
            isPostCode
        },
        houseNumber: {
            required
        },
        firstName: {
            required
        },
        emailAddress: {
            required,
            email
        },
        consent: {
            required,
            mustBeTrue
        }
    }
};
</script>

<style lang="scss">
@import 'src/sass/vue-utils.scss';
@import 'src/sass/main';

html, body {
    width: 100vw;
}

h1 {
    display: inline-flex;
    align-items: flex-end;
    font-size: 1.4rem;
    font-weight: bold;
    margin-bottom: 15px;

    img {
        height: 22px;
        margin: 0 0 2px 8px;
    }
}

section {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    overflow-y: auto;
    padding: 20px;

    &.has-scrollbar {
        padding-right: 10px;

        h1 {
            margin-right: -20px;
        }

        form {
            padding-bottom: 20px;
        }
    }
}

form {
    position: relative;
}

.field {
    &.is-grouped {
        .field {
            width: calc(50% - 0.375rem);
        }

        margin-bottom: 0;
    }

    .is-danger {
        border-width: 2px;
    }

    .is-success, .is-success:focus {
        border-color: $grey-lighter;
    }

    .label {
        font-weight: normal;
    }

    .b-checkbox {
        align-items: flex-start;

        &.is-danger {
            .check {
                border: 2px solid $danger !important;
            }
        }
    }

    .input {
        font-size: 1.1rem;
    }

    .icon {
        svg {
            width: 1.15rem;
            height: auto;
            margin-top: -.25rem;
        }

        &.has-text-danger {
            display: none;
        }
    }
}

ul.errors {
    padding: 12px 15px;
    background-color: $danger-light;
    border-radius: 5px;
    list-style: none;
    margin-bottom: 10px;

    li {
        color: $white;
        margin-bottom: 5px;

        &:last-child {
            margin: 0;
        }
    }
}
</style>
